import type { Uuid } from "@shared/api";

/** Куда можно перейти из быстрого перехода. */
export type Target =
  | { kind: "server"; id: Uuid; name: string; iconId?: Uuid | null }
  | { kind: "channel"; id: Uuid; name: string; serverId: Uuid; serverName: string; announcement?: boolean }
  | { kind: "direct"; id: Uuid; name: string; handle: string }
  /** Друг, с которым переписки ещё нет: выбор её начинает — «начать беседу» Discord. */
  | { kind: "person"; id: Uuid; name: string; handle: string };

/**
 * Что подходит под набранное — как в Discord: начало слова важнее середины,
 * начало имени — важнее начала слова. Первый знак сужает вид: `#` — каналы,
 * `@` — переписки и друзья, `*` — серверы.
 *
 * Пустой запрос — тоже ответ: переписки и серверы по порядку, без каналов и
 * друзей без переписки. Их десятки, и без запроса они затопили бы список.
 */
export function search(targets: readonly Target[], raw: string, limit = 8): Target[] {
  const prefix = raw.trimStart()[0];
  const only =
    prefix === "#" ? "channel" : prefix === "@" ? "direct" : prefix === "*" ? "server" : null;
  const query = (only ? raw.trimStart().slice(1) : raw).trim().toLowerCase();
  const allowed = targets.filter(
    (target) => !only || target.kind === only || (only === "direct" && target.kind === "person"),
  );

  if (!query) {
    return allowed
      .filter((target) => target.kind !== "person" && (only === "channel" || target.kind !== "channel"))
      .slice(0, limit);
  }

  return allowed
    .map((target, order) => ({ target, order, score: score(target, query) }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .slice(0, limit)
    .map((item) => item.target);
}

function score(target: Target, query: string): number {
  const names = [target.name, ...(target.kind === "direct" || target.kind === "person" ? [target.handle] : [])].map((name) =>
    name.toLowerCase(),
  );
  let best = 0;

  for (const name of names) {
    if (name === query) best = Math.max(best, 4);
    else if (name.startsWith(query)) best = Math.max(best, 3);
    else if (name.split(/[\s_\-#.]+/).some((word) => word.startsWith(query))) best = Math.max(best, 2);
    else if (name.includes(query)) best = Math.max(best, 1);
  }

  return best;
}
